/**
 * ImprovementSuggestions Component
 * Actionable recommendations list based on weak performance areas
 */

import React from 'react';
import { clsx } from 'clsx';
import { Card } from '../common';
import { Lightbulb, ArrowUpRight, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import type { Improvement } from '../../types';

export interface ImprovementSuggestionsProps {
  improvements: Improvement[];
  title?: string;
  className?: string;
  isLoading?: boolean;
}

const PRIORITY_CONFIG = {
  high: {
    icon: AlertCircle,
    iconColor: 'text-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-l-red-500',
    label: 'High Priority',
    labelClass: 'bg-red-100 text-red-700',
  },
  medium: {
    icon: AlertTriangle,
    iconColor: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-l-amber-500',
    label: 'Medium Priority',
    labelClass: 'bg-amber-100 text-amber-700',
  },
  low: {
    icon: Info,
    iconColor: 'text-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-l-blue-500',
    label: 'Low Priority',
    labelClass: 'bg-blue-100 text-blue-700',
  },
};

export const ImprovementSuggestions: React.FC<ImprovementSuggestionsProps> = ({
  improvements,
  title = 'Improvement Suggestions',
  className,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="animate-pulse">
          <div className="h-5 bg-stone-200 rounded w-44 mb-6" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="mb-4 p-4 bg-stone-50 rounded-lg">
              <div className="h-4 bg-stone-200 rounded w-24 mb-2" />
              <div className="h-5 bg-stone-200 rounded w-full mb-2" />
              <div className="h-4 bg-stone-200 rounded w-3/4" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (!improvements || improvements.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-800 mb-4">{title}</h3>
        <div className="text-center py-8 bg-green-50 rounded-lg">
          <Lightbulb className="w-12 h-12 mx-auto mb-2 text-green-500" />
          <p className="text-green-700 font-medium">Great job!</p>
          <p className="text-green-600 text-sm">
            No major improvement areas identified
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      <div className="flex items-center gap-2 mb-6">
        <Lightbulb className="w-5 h-5 text-amber-500" />
        <h3 className="text-lg font-semibold text-stone-800">{title}</h3>
      </div>

      <div className="space-y-4">
        {improvements.map((improvement, index) => {
          const config = PRIORITY_CONFIG[improvement.priority];
          const PriorityIcon = config.icon;

          return (
            <div
              key={index}
              className={clsx(
                'p-4 rounded-lg border-l-4',
                config.bgColor,
                config.borderColor
              )}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <PriorityIcon className={clsx('w-5 h-5', config.iconColor)} />
                  <span className="font-medium text-stone-800">
                    {improvement.area}
                  </span>
                </div>
                <span
                  className={clsx(
                    'px-2 py-0.5 rounded text-xs font-medium',
                    config.labelClass
                  )}
                >
                  {config.label}
                </span>
              </div>

              {/* Recommendation */}
              <p className="text-stone-700 mb-3">{improvement.recommendation}</p>

              {/* Stats */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">
                  Current score:{' '}
                  <span className="font-medium text-stone-700">
                    {Math.round(improvement.currentScore)}
                  </span>
                </span>
                <span className="flex items-center gap-1 text-green-600">
                  <ArrowUpRight className="w-4 h-4" />
                  <span>
                    Potential gain:{' '}
                    <span className="font-medium">
                      +{Math.round(improvement.potentialGain)} pts
                    </span>
                  </span>
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <p className="mt-4 text-xs text-stone-400 text-center">
        Improvements are ranked by potential impact on your overall score
      </p>
    </Card>
  );
};

export default ImprovementSuggestions;
